import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, DollarSign, Plus, Minus, CreditCard, 
  Users, AlertCircle, CheckCircle, TrendingUp, TrendingDown,
  Search, RefreshCw, Filter, AlertTriangle, Wallet, ChevronRight,
  Sparkles, Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 50
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.8,
    y: 50,
    transition: {
      duration: 0.2
    }
  }
};

const alertVariants = {
  hidden: { opacity: 0, x: -50, scale: 0.95 },
  visible: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20
    }
  },
  exit: { 
    opacity: 0, 
    x: 50,
    transition: { duration: 0.2 }
  }
};

const AccountManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [operation, setOperation] = useState('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadAllAccounts();
  }, [user]);

  const loadAllAccounts = async () => {
    try {
      setLoading(true);
      setIsRefreshing(true);
      const response = await adminAPI.getAllAccounts();
      setAccounts(response.data);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Detect and filter duplicate accounts
  const { uniqueAccounts, duplicateCount } = useMemo(() => {
    const seen = new Map();
    const unique = [];
    let dupes = 0;

    accounts.forEach(acc => {
      const key = acc.account_number.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, acc);
        unique.push(acc);
      } else {
        dupes++;
      }
    });

    return { uniqueAccounts: unique, duplicateCount: dupes };
  }, [accounts]);

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return uniqueAccounts;
    const query = searchQuery.toLowerCase();
    return uniqueAccounts.filter(acc => 
      acc.account_number.toLowerCase().includes(query) ||
      acc.iban?.toLowerCase().includes(query) ||
      acc.bank_name?.toLowerCase().includes(query)
    );
  }, [uniqueAccounts, searchQuery]);

  const handleBalanceUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for this operation');
      return;
    }

    try {
      const response = await adminAPI.updateAccountBalance(selectedAccount.id, {
        amount: parseFloat(amount),
        operation: operation,
        reason: reason
      });

      setSuccess(response.data.message);
      setShowModal(false);
      setAmount('');
      setReason('');
      loadAllAccounts();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update balance');
    }
  };

  if (loading && !isRefreshing) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center cyber-grid">
        <div className="text-center">
          <motion.div
            className="relative"
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
              scale: { duration: 1, repeat: Infinity }
            }}
          >
            <CreditCard className="w-16 h-16 text-terminal-green" />
            <motion.div
              className="absolute inset-0 border-2 border-terminal-green rounded-lg"
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [1, 0, 1]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-4 text-terminal-green-dim text-sm"
          >
            LOADING ACCOUNTS...
          </motion.p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-terminal-green p-4 md:p-8 cyber-grid">
      <div className="scanline" />
      
      <motion.div 
        className="max-w-7xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div
          variants={cardVariants}
          className="mb-8"
        >
          <motion.button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-terminal-green-dim hover:text-terminal-green mb-4 group"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
            data-testid="back-button"
          >
            <motion.div
              animate={{ x: [0, -3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowLeft className="w-4 h-4" />
            </motion.div>
            <span className="group-hover:glow-text-static transition-all">BACK TO DASHBOARD</span>
          </motion.button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <motion.h1 
                className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 100 }}
              >
                ACCOUNT MANAGEMENT
              </motion.h1>
              <motion.p 
                className="text-terminal-green-dim text-sm flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Wallet className="w-4 h-4" />
                Full Control Over All System Accounts
              </motion.p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadAllAccounts}
              className="terminal-button px-4 py-2 flex items-center gap-2"
              data-testid="refresh-button"
            >
              <motion.div
                animate={isRefreshing ? { rotate: 360 } : {}}
                transition={{ duration: 1, repeat: isRefreshing ? Infinity : 0, ease: "linear" }}
              >
                <RefreshCw className="w-4 h-4" />
              </motion.div>
              <span>REFRESH</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Alerts */}
        <AnimatePresence mode="sync">
          {success && (
            <motion.div
              key="success"
              variants={alertVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="mb-6 p-4 border border-terminal-green bg-terminal-green/10 flex items-center gap-2 success-pulse"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircle className="w-5 h-5 text-terminal-green" />
              </motion.div>
              <span>{success}</span>
              <motion.button 
                onClick={() => setSuccess('')} 
                className="ml-auto hover:text-white"
                whileHover={{ scale: 1.2, rotate: 90 }}
              >
                x
              </motion.button>
            </motion.div>
          )}

          {error && (
            <motion.div
              key="error"
              variants={alertVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="mb-6 p-4 border border-red-500 bg-red-500/10 flex items-center gap-2 error-shake"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-500">{error}</span>
              <motion.button 
                onClick={() => setError('')} 
                className="ml-auto text-red-500 hover:text-red-300"
                whileHover={{ scale: 1.2, rotate: 90 }}
              >
                x
              </motion.button>
            </motion.div>
          )}

          {duplicateCount > 0 && (
            <motion.div
              key="duplicate"
              variants={alertVariants}
              initial="hidden"
              animate="visible"
              className="mb-6 p-4 border border-yellow-500 bg-yellow-500/10 flex items-center gap-2"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
              >
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </motion.div>
              <span className="text-yellow-500">
                {duplicateCount} duplicate account entries detected and filtered out
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Bar */}
        <motion.div
          variants={cardVariants}
          className="mb-6"
        >
          <div className="relative group">
            <motion.div
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-terminal-green-dim group-focus-within:text-terminal-green transition-colors"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Search className="w-4 h-4" />
            </motion.div>
            <input
              type="text"
              placeholder="Search accounts by number, IBAN, or bank name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 terminal-input"
              data-testid="search-input"
            />
            {searchQuery && (
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-terminal-green-dim hover:text-terminal-green"
                whileHover={{ rotate: 90 }}
              >
                x
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          variants={cardVariants}
          className="mb-6 p-4 border border-terminal-green/30 bg-terminal-green/5 flex flex-wrap gap-6"
        >
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-terminal-green-dim" />
            <span className="text-sm text-terminal-green-dim">Total Accounts:</span>
            <motion.span 
              className="font-bold"
              key={filteredAccounts.length}
              initial={{ scale: 1.5, color: "#00ff41" }}
              animate={{ scale: 1 }}
            >
              {filteredAccounts.length}
            </motion.span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-terminal-green-dim" />
            <span className="text-sm text-terminal-green-dim">Total Balance:</span>
            <motion.span 
              className="font-bold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              ${filteredAccounts.reduce((sum, acc) => sum + acc.balance, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </motion.span>
          </div>
        </motion.div>

        {/* Accounts Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
        >
          <AnimatePresence mode="popLayout">
            {filteredAccounts.map((account, index) => (
              <motion.div
                key={account.id}
                layout
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 10px 40px rgba(0, 255, 65, 0.2)"
                }}
                className="terminal-card p-6 relative overflow-hidden group"
                data-testid={`account-card-${index}`}
              >
                {/* Animated background */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-terminal-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
                
                {/* Top accent line */}
                <motion.div
                  className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-terminal-green to-transparent"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-xs text-terminal-green-dim">ACCOUNT</div>
                      <motion.div 
                        className="text-lg font-bold font-mono"
                        whileHover={{ scale: 1.02 }}
                      >
                        {account.account_number}
                      </motion.div>
                    </div>
                    <motion.div 
                      className="text-xs px-2 py-1 border border-terminal-green"
                      whileHover={{ 
                        scale: 1.1,
                        boxShadow: "0 0 10px rgba(0, 255, 65, 0.5)"
                      }}
                    >
                      {account.currency}
                    </motion.div>
                  </div>

                  <div className="mb-4">
                    <div className="text-xs text-terminal-green-dim mb-1">BALANCE</div>
                    <motion.div 
                      className="text-2xl font-bold"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      ${account.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </motion.div>
                  </div>

                  {account.iban && (
                    <motion.div 
                      className="mb-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div className="text-xs text-terminal-green-dim mb-1">IBAN</div>
                      <div className="text-xs font-mono text-terminal-green-dim">{account.iban}</div>
                    </motion.div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSelectedAccount(account);
                        setOperation('add');
                        setShowModal(true);
                      }}
                      className="py-2 px-3 border border-terminal-green hover:bg-terminal-green/20 flex items-center justify-center gap-2 text-sm relative overflow-hidden group/btn"
                      data-testid={`add-funds-${index}`}
                    >
                      <motion.div
                        className="absolute inset-0 bg-terminal-green/10"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 0.5 }}
                      />
                      <Plus className="w-4 h-4 relative z-10" />
                      <span className="relative z-10">ADD</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSelectedAccount(account);
                        setOperation('subtract');
                        setShowModal(true);
                      }}
                      className="py-2 px-3 border border-red-500 hover:bg-red-500/20 flex items-center justify-center gap-2 text-sm text-red-500 relative overflow-hidden"
                      data-testid={`subtract-funds-${index}`}
                    >
                      <motion.div
                        className="absolute inset-0 bg-red-500/10"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 0.5 }}
                      />
                      <Minus className="w-4 h-4 relative z-10" />
                      <span className="relative z-10">SUBTRACT</span>
                    </motion.button>
                  </div>

                  <motion.div 
                    className="mt-4 pt-4 border-t border-terminal-green/30 text-xs text-terminal-green-dim flex items-center justify-between"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <span>Transfers: {account.previous_transfers}</span>
                    <motion.div
                      animate={{ x: [0, 3, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Empty State */}
        {filteredAccounts.length === 0 && (
          <motion.div 
            className="text-center py-12 text-terminal-green-dim"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
            </motion.div>
            <p className="text-lg">No accounts found</p>
            <p className="text-sm mt-2">Try adjusting your search query</p>
          </motion.div>
        )}
      </motion.div>

      {/* Balance Update Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="terminal-card p-8 w-full max-w-md relative overflow-hidden"
            >
              {/* Animated border glow */}
              <motion.div
                className="absolute inset-0 border-2 border-terminal-green/50"
                animate={{ 
                  boxShadow: [
                    "0 0 10px rgba(0, 255, 65, 0.3)",
                    "0 0 30px rgba(0, 255, 65, 0.5)",
                    "0 0 10px rgba(0, 255, 65, 0.3)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
                  {operation === 'add' ? (
                    <>
                      <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <TrendingUp className="w-5 h-5" />
                      </motion.div>
                      <h2 className="text-xl font-bold cyber-font">ADD FUNDS</h2>
                    </>
                  ) : (
                    <>
                      <motion.div
                        animate={{ y: [0, 3, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <TrendingDown className="w-5 h-5 text-red-500" />
                      </motion.div>
                      <h2 className="text-xl font-bold cyber-font text-red-500">SUBTRACT FUNDS</h2>
                    </>
                  )}
                </div>

                <motion.div 
                  className="mb-4 p-3 border border-terminal-green/30 bg-terminal-green/5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="text-xs text-terminal-green-dim">TARGET ACCOUNT</div>
                  <div className="font-bold font-mono">{selectedAccount?.account_number}</div>
                  <div className="text-sm mt-1">
                    Current Balance: <span className="font-bold">${selectedAccount?.balance.toLocaleString()}</span>
                  </div>
                </motion.div>

                <form onSubmit={handleBalanceUpdate} className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <label className="block text-sm mb-2">AMOUNT ({selectedAccount?.currency})</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-3 terminal-input text-lg font-bold"
                      placeholder="0.00"
                      required
                      data-testid="amount-input"
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <label className="block text-sm mb-2">REASON / NOTES</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full px-4 py-3 terminal-input"
                      rows="3"
                      placeholder="Provide a reason for this operation..."
                      required
                      data-testid="reason-input"
                    />
                  </motion.div>

                  <motion.div 
                    className="flex gap-2 pt-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <motion.button
                      type="submit"
                      className={`flex-1 terminal-button py-3 ${operation === 'subtract' ? 'border-red-500 text-red-500 hover:bg-red-500/20' : ''}`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="submit-balance-update"
                    >
                      {operation === 'add' ? 'ADD FUNDS' : 'SUBTRACT FUNDS'}
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setAmount('');
                        setReason('');
                        setError('');
                      }}
                      className="flex-1 terminal-button py-3"
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="cancel-button"
                    >
                      CANCEL
                    </motion.button>
                  </motion.div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountManagement;
